import React from 'react';
import { Calendar, MapPin, Trophy, Building2 } from 'lucide-react';
import { timeline } from '../data/mockData';

const Tiane = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-32 brick-texture overflow-hidden">
        <div 
          className="absolute inset-0 z-0 opacity-20"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_028c76be-fda9-4451-9c6f-c3c6a93b4016/artifacts/xy0xl5gt_IMG_0015.jpeg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-block mb-4">
              <div className="text-4xl font-bold text-gold-gradient">CE</div>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-muro-text">
              Christiane <span className="text-gold-gradient">Endler</span>
            </h1>
            <p className="text-2xl text-muro-text-muted">
              La mejor arquera del mundo
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-6">
              <div className="bg-muro-gold/10 backdrop-blur-sm px-6 py-3 rounded-lg border border-muro-gold/20">
                <p className="text-muro-gold font-semibold">1,83m</p>
                <p className="text-muro-text-muted text-sm">Altura</p>
              </div>
              <div className="bg-muro-gold/10 backdrop-blur-sm px-6 py-3 rounded-lg border border-muro-gold/20">
                <p className="text-muro-gold font-semibold">34 años</p>
                <p className="text-muro-text-muted text-sm">Edad</p>
              </div>
              <div className="bg-muro-gold/10 backdrop-blur-sm px-6 py-3 rounded-lg border border-muro-gold/20">
                <p className="text-muro-gold font-semibold">Lyon</p>
                <p className="text-muro-text-muted text-sm">Club Actual</p>
              </div>
              <div className="bg-muro-gold/10 backdrop-blur-sm px-6 py-3 rounded-lg border border-muro-gold/20">
                <p className="text-muro-gold font-semibold">Capitana</p>
                <p className="text-muro-text-muted text-sm">Chile</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Biography */}
      <section className="py-20 bg-muro-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-muro-text mb-4 gold-accent-line inline-block">
                La Historia de Tiane
              </h2>
            </div>

            <div className="space-y-6 text-muro-text-muted leading-relaxed">
              <p className="text-lg">
                <span className="text-muro-gold font-semibold">Claudia Christiane Endler Mutinelli</span> nació el 23 de julio de 1991 en Santiago de Chile. Desde pequeña, en el patio de la casa familiar en Las Condes, su hermano mayor Nicolás la convertía en su sparring particular, lanzándole pelotazos sin piedad.
              </p>
              
              <p>
                A los 15 años, siendo delantera, <span className="text-muro-gold">Marco Antonio Cornez</span> vio algo diferente en ella: "Con esa altura y esos reflejos... vas a ser la mejor arquera de Chile". Ese día cambió todo. Tiane dejó de meter goles para empezar a pararlos.
              </p>

              <p>
                Su carrera profesional comenzó en <span className="text-muro-gold">Everton de Viña del Mar</span>, donde paró dos penales en su debut. En 2011 llegó a <span className="text-muro-gold">Colo-Colo</span>, el club más grande de Chile, donde a los 20 años la nombraron capitana. Ahí empezó su obsesión por profesionalizar el fútbol femenino chileno.
              </p>

              <p>
                En 2012 ganó su primera <span className="text-muro-gold">Copa Libertadores</span> con Colo-Colo, parando penales decisivos en la final. Era solo el comienzo.
              </p>

              <p>
                2017 marcó su salto a Europa con el <span className="text-muro-gold">Valencia CF</span>. Un año después llegó al <span className="text-muro-gold">Paris Saint-Germain</span>, donde ganó múltiples ligas y copas francesas. Pero el sueño grande aún faltaba.
              </p>

              <p>
                En 2019, como capitana de Chile, disputó su primer <span className="text-muro-gold">Mundial Femenino</span> en Francia. Sus atajadas contra Estados Unidos la pusieron en el mapa mundial. El mismo año fue nominada por primera vez al <span className="text-muro-gold">The Best FIFA</span>.
              </p>

              <p>
                2021 fue el año del gran salto: fichó por el <span className="text-muro-gold">Olympique de Lyon</span>, el club más ganador de Europa. Y llegó el momento definitivo: <span className="text-muro-gold font-semibold">21 de mayo de 2022, Turín</span>. Final de la Champions League. Lyon vs Barcelona. Esa mañana había muerto Marco Cornez, el hombre que la descubrió. Tiane lloró, pero salió al campo y fue MVP del partido. Lyon ganó 3-1. Primera chilena campeona de Europa.
              </p>

              <p>
                Hoy, con 34 años, sigue siendo titular indiscutida en Lyon y capitana de Chile. Sigue llegando una hora antes a los entrenamientos. Sigue diciendo: <span className="text-muro-gold italic">"Siempre se puede mejorar más"</span>.
              </p>

              <p className="text-xl text-muro-gold italic text-center pt-6">
                "El muro más fuerte no es el que nunca recibe golpes. Es el que los recibe todos... y sigue en pie."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 brick-texture">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-muro-text mb-4">
                Timeline de su <span className="text-gold-gradient">Carrera</span>
              </h2>
              <p className="text-muro-text-muted text-lg">
                Desde Las Condes hasta el Olimpo europeo
              </p>
            </div>

            {/* Timeline Container */}
            <div className="relative">
              {/* Vertical Line */}
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-1 bg-muro-gray-700 transform md:-translate-x-1/2"></div>

              {/* Timeline Items */}
              <div className="space-y-12">
                {timeline.map((item, index) => (
                  <div 
                    key={index} 
                    className={`relative flex items-center ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Year Badge */}
                    <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 w-16 h-16 bg-muro-gold rounded-full flex items-center justify-center shadow-gold-lg z-10">
                      <span className="text-muro-black font-bold text-sm">
                        {item.year}
                      </span>
                    </div>

                    {/* Content Card */}
                    <div className={`ml-24 md:ml-0 ${
                      index % 2 === 0 ? 'md:mr-auto md:pr-16' : 'md:ml-auto md:pl-16'
                    } md:w-5/12`}>
                      <div className="glass-morphism p-6 rounded-lg border border-muro-gray-800 card-hover">
                        {item.trophy && (
                          <div className="flex items-center space-x-2 mb-2">
                            <Trophy className="w-4 h-4 text-muro-gold" />
                            <span className="text-muro-gold text-xs font-semibold uppercase">
                              {item.trophy}
                            </span>
                          </div>
                        )}
                        <h3 className="text-2xl font-bold text-muro-text mb-2">
                          {item.title}
                        </h3>
                        <p className="text-muro-text-muted mb-4">
                          {item.description}
                        </p>
                        <div className="flex flex-wrap gap-3 text-sm">
                          <div className="flex items-center space-x-2 text-muro-text-muted">
                            <MapPin className="w-4 h-4" />
                            <span>{item.location}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-muro-text-muted">
                            <Building2 className="w-4 h-4" />
                            <span>{item.club}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quotes Section */}
      <section className="py-20 bg-muro-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-muro-text mb-4">
                Sus <span className="text-gold-gradient">Palabras</span>
              </h2>
            </div>

            <div className="space-y-8">
              {[
                {
                  quote: "Intento aprender día a día, siempre se puede mejorar más.",
                  context: "Sobre su filosofía de trabajo"
                },
                {
                  quote: "El fútbol me dio todo lo que tengo. Pero yo le devolví algo mucho más grande: una forma de ser.",
                  context: "Reflexión sobre su carrera"
                },
                {
                  quote: "Quiero que las niñas chilenas tengan lo que yo no tuve.",
                  context: "Sobre profesionalizar el fútbol femenino"
                },
                {
                  quote: "El verdadero liderazgo no es el que más se oye. Es el que más se siente.",
                  context: "Sobre ser capitana"
                }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="glass-morphism p-8 rounded-lg border-l-4 border-muro-gold"
                >
                  <p className="text-2xl text-muro-text italic mb-4">
                    "{item.quote}"
                  </p>
                  <p className="text-muro-text-muted text-sm">
                    — {item.context}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="py-20 brick-texture">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-muro-text mb-4">
                El Muro en <span className="text-gold-gradient">Números</span>
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { number: '20+', label: 'Años de Carrera' },
                { number: '1', label: 'Champions League' },
                { number: '5', label: 'Ligas Francesas' },
                { number: '5', label: 'Títulos en Chile' },
                { number: '100+', label: 'Partidos con Chile' },
                { number: '1', label: 'Libertadores' },
                { number: '1', label: 'Copa del Mundo' },
                { number: '∞', label: 'Sueños inspirados' }
              ].map((stat, index) => (
                <div 
                  key={index}
                  className="glass-morphism p-6 rounded-lg text-center card-hover"
                >
                  <div className="text-4xl font-bold text-gold-gradient mb-2">
                    {stat.number}
                  </div>
                  <p className="text-muro-text-muted text-sm">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tiane;
